import type { ExportCSVResponse } from '../components/DataGrid/types';
import { URL_PREFIX } from '../constants/api';

export interface TrackLock {
  isrc: string;
  is_active: boolean;
  created_at: string;
  created_by: {
    email: string;
    id: number;
    name: string;
  };
}

export interface HydratedTrackLock {
  isrc: string;
  is_active: boolean;
  major_label: string[];
  roll_up_title: string;
  presentation_label: string[];
}

export interface PageDataResponse {
  data: Partial<HydratedTrackLock>[];
  has_next: boolean;
}

export async function getTrackLocks(isrc: string): Promise<TrackLock[]> {
  const resp = await fetch(`${URL_PREFIX}/api/v1/track-lock/${isrc}`);
  if (!resp.ok) {
    throw new Error(`Failed to fetch track locks: ${resp.statusText}`);
  }
  return (await resp.json()) as TrackLock[];
}

export async function getTrackLock(isrc: string): Promise<TrackLock | null> {
  const resp = await fetch(`${URL_PREFIX}/api/v1/track-lock/active/${isrc}`);
  if (resp.status === 404) {
    return null;
  }
  if (!resp.ok) {
    throw new Error(`Failed to fetch track lock: ${resp.statusText}`);
  }
  return (await resp.json()) as TrackLock;
}

export async function unlockTrack(isrc: string): Promise<TrackLock> {
  const resp = await fetch(`${URL_PREFIX}/api/v1/track-lock/close/${isrc}`, {
    method: 'PATCH',
  });
  if (!resp.ok) {
    throw new Error(`Failed to unlock track: ${resp.statusText}`);
  }
  return (await resp.json()) as TrackLock;
}

export async function lockTrack(isrc: string): Promise<TrackLock> {
  const resp = await fetch(`${URL_PREFIX}/api/v1/track-lock/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isrc }),
  });
  if (!resp.ok) {
    throw new Error(`Failed to lock track: ${resp.statusText}`);
  }
  return (await resp.json()) as TrackLock;
}

export async function getLocksBulk(isrcs: string[]): Promise<TrackLock[]> {
  const resp = await fetch(`${URL_PREFIX}/api/v1/track-lock/active`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isrcs }),
  });
  if (!resp.ok) {
    throw new Error(`Failed to fetch bulk locks: ${resp.statusText}`);
  }
  return (await resp.json()) as TrackLock[];
}

export async function getCurrentUserTrackLock(
  limit = 100,
  offset = 0,
): Promise<PageDataResponse> {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());

  const resp = await fetch(
    `${URL_PREFIX}/api/v1/track-lock/user/me?${params.toString()}`,
  );
  if (!resp.ok) {
    throw new Error(`Failed to fetch user track locks: ${resp.statusText}`);
  }
  return (await resp.json()) as PageDataResponse;
}

export async function exportCurrentUserTrackLocks(): Promise<ExportCSVResponse> {
  const resp = await fetch(`${URL_PREFIX}/api/v1/track-lock/user/me/export`);
  if (!resp.ok) {
    throw new Error(`Failed to export track locks: ${resp.statusText}`);
  }
  return (await resp.json()) as ExportCSVResponse;
}

export async function bulkLockTracks(isrcs: string[]): Promise<void> {
  const chunkSize = 25;

  for (let i = 0; i < isrcs.length; i += chunkSize) {
    const chunk = isrcs.slice(i, i + chunkSize);
    const resp = await fetch(`${URL_PREFIX}/api/v1/track-lock/create/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isrcs: chunk }),
    });
    if (!resp.ok) {
      throw new Error(`Failed to bulk lock tracks: ${resp.statusText}`);
    }
  }
}

export async function bulkUnlockTracks(isrcs: string[]): Promise<void> {
  const chunkSize = 25;

  for (let i = 0; i < isrcs.length; i += chunkSize) {
    const chunk = isrcs.slice(i, i + chunkSize);
    const resp = await fetch(`${URL_PREFIX}/api/v1/track-lock/close/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isrcs: chunk }),
    });
    if (!resp.ok) {
      throw new Error(`Failed to bulk unlock tracks: ${resp.statusText}`);
    }
  }
}
